import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { startTableSessionConnection } from '../Utills/tableSessionConnection';

const TableSessionContext = createContext(() => () => {});
const read = key => {
  try { return JSON.parse(localStorage.getItem(key) || 'null'); }
  catch { return null; }
};

export function TableSessionProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Session storage is populated by the existing API flow before Redux updates.
  useSelector(state => state.user);
  const [, storageChanged] = useState(0);
  const subscribers = useRef(new Map());
  const liveModification = useRef(null);
  const activeSession = useRef(null);
  const route = useRef(null);
  const [restaurant, restaurantId, tableNumber, page] = location.pathname.split('/').filter(Boolean);
  route.current = { restaurant, restaurantId, tableNumber, page, navigate };
  const user = read('userInfo');
  const stored = read('allInfo');
  const authenticated = Boolean(localStorage.getItem('authRefreshToken'));
  const sameUserTable = user?.restaurantId?.toLowerCase() === restaurantId?.toLowerCase()
    && String(user?.tableNumber) === tableNumber;
  const validStored = stored?.tableOtp && stored.isActive !== false
    && String(stored.restaurantOrBranchId || user?.restaurantId).toLowerCase() === restaurantId?.toLowerCase()
    && String(stored.tableNumber ?? user?.tableNumber) === tableNumber;
  if (!authenticated || !sameUserTable || tableNumber === 'cat'
    || localStorage.getItem('guestUser') === 'true'
    || ['PaymentStatus', 'thank-you'].includes(page)) {
    activeSession.current = null;
  } else if (validStored) {
    activeSession.current = { restaurantId: restaurantId.toLowerCase(), tableNumber, tableOtp: String(stored.tableOtp).trim() };
  } else if (stored || activeSession.current?.restaurantId !== restaurantId?.toLowerCase()
    || activeSession.current?.tableNumber !== tableNumber) {
    activeSession.current = null;
  }
  // Keep a tab's subscription if another tab clears allInfo while the same
  // broadcast is in flight. Each tab must process its own ReceiveBillStatus.
  const sessionId = activeSession.current?.restaurantId;
  const sessionOtp = activeSession.current?.tableOtp;
  const sessionKey = sessionId && sessionOtp ? `${sessionId}_${sessionOtp}` : null;
  if (liveModification.current?.sessionKey !== sessionKey) liveModification.current = { sessionKey, data: null };
  const subscribe = useCallback((event, callback) => {
    if (!subscribers.current.has(event)) subscribers.current.set(event, new Set());
    const listeners = subscribers.current.get(event);
    listeners.add(callback);
    if (event === 'ReceiveLiveBillModification' && liveModification.current?.data)
      callback(liveModification.current.data);
    return () => listeners.delete(callback);
  }, []);

  useEffect(() => {
    const onStorage = event => {
      if (!event.key || ['allInfo', 'userInfo', 'authRefreshToken'].includes(event.key)) storageChanged(n => n + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    if (!sessionId || !sessionOtp) return;
    return startTableSessionConnection({ restaurantId: sessionId, tableOtp: sessionOtp }, (event, data) => {
      if (event === 'ReceiveLiveBillModification')
        liveModification.current.data = { ...liveModification.current.data, ...data };
      const listeners = [...(subscribers.current.get(event) || [])];
      if (listeners.length) {
        listeners.forEach(listener => listener(data));
      } else if (event === 'ReceiveBillStatus') {
        const current = route.current;
        if (data.isPaid === true || data.status === 'Paid' || data.status === 'Void') {
          localStorage.removeItem('allInfo');
          current.navigate(`/${current.restaurant}/${current.restaurantId}/${current.tableNumber}/PaymentStatus/${data.status}`);
        } else {
          toast.info(data.message || `Bill status: ${data.status}`);
        }
      }
    });
  }, [sessionId, sessionOtp]);

  return <TableSessionContext.Provider value={subscribe}>{children}</TableSessionContext.Provider>;
}

export function useTableSessionEvent(event, callback) {
  const subscribe = useContext(TableSessionContext);
  const latest = useRef(callback);
  latest.current = callback;
  useEffect(() => subscribe(event, data => latest.current(data)), [subscribe, event]);
}
